// src/containers/preview/DemoGame.js
//
// A topic's REAL game for the public demo: the same game component, the same
// "Questions done!" card, the same bonus round and the same finish screen a
// student gets — with nothing saved. It plays full screen over the home page.
//
// How it stays harmless (same approach as Layout Builder ▸ Game library):
//   • the game runs inside its own MemoryRouter, isolated from the site's
//     router, so a game's "Return" / navigate() can't move the home page;
//     any navigation away simply closes the game.
//   • no UniversalQuizGame: that component is the one that writes progress,
//     the leaderboard and coins. Here the scores just come back to the page.

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as ReactRouterDOM from "react-router-dom";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import ModalPortal from "../../components/common/ModalPortal";
import BonusRouter from "../../components/quizzes/BonusRouter";
import { GameExitProvider } from "../../components/quizzes/gameExit";
import { GameDone, RoundDone } from "../../components/quizzes/kit/Celebration";
import { PLACEHOLDER, parseMathGameType, resolveGameType, scoreOf } from "../../config/gameRegistry";
import { answersFromPayload } from "./demoContent";

/* ------------------------------------------------------------------ */
/* Router isolation                                                    */
/* ------------------------------------------------------------------ */

const LocationContext = ReactRouterDOM.UNSAFE_LocationContext;
const RouteContext = ReactRouterDOM.UNSAFE_RouteContext;
const CAN_ISOLATE = Boolean(LocationContext && RouteContext);
const EMPTY_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false };

const ExitSignal = ({ onExit }) => {
  useEffect(() => {
    onExit?.();
  }, [onExit]);
  return null;
};

const GameFrame = ({ entry, onExit, children }) => {
  if (!CAN_ISOLATE) {
    return <p className="pvd-game-msg">This game can't be previewed here. Sign up to play every game in the portal!</p>;
  }
  return (
    <RouteContext.Provider value={EMPTY_ROUTE_CONTEXT}>
      <LocationContext.Provider value={null}>
        <MemoryRouter initialEntries={[entry]}>
          <Routes>
            <Route
              path="/games/:state/:grade/:subject/:view/:slug"
              element={<GameExitProvider returnPath="/__demo_exit__">{children}</GameExitProvider>}
            />
            <Route path="*" element={<ExitSignal onExit={onExit} />} />
          </Routes>
        </MemoryRouter>
      </LocationContext.Provider>
    </RouteContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/* Which component plays this topic                                    */
/* ------------------------------------------------------------------ */

const lazyCache = new Map();
const lazyFor = (key, loader) => {
  if (!lazyCache.has(key)) lazyCache.set(key, React.lazy(loader));
  return lazyCache.get(key);
};

// Classroom-only games (they need a live class session) fall back to the
// plain multiple-choice game for a visitor on their own.
const NOT_SOLO = new Set(["classQuizShow"]);

const pickGame = (gameType, questions) => {
  const math = parseMathGameType(gameType);
  if (math) {
    return { key: "__math__", load: () => import("../../components/quizzes/UniversalMathGame"), math };
  }
  let entry = resolveGameType(gameType);
  if (!entry || NOT_SOLO.has(entry.id)) {
    // shape of the questions decides the safest fallback
    const q = questions[0] || {};
    entry = resolveGameType(Array.isArray(q.answers) ? "multipleChoice" : Array.isArray(q.pairs) ? "matching" : Array.isArray(q.options) ? "dragDrop" : "multipleChoice");
  }
  return { key: entry.id, load: entry.load, math: null };
};

const shuffle = (list = []) =>
  [...list]
    .map((q) => ({
      ...q,
      answers: Array.isArray(q.answers) ? [...q.answers].sort(() => Math.random() - 0.5) : q.answers,
      options: Array.isArray(q.options) ? [...q.options].sort(() => Math.random() - 0.5) : q.options,
    }))
    .sort(() => Math.random() - 0.5);

/* ------------------------------------------------------------------ */
/* Player                                                              */
/* ------------------------------------------------------------------ */

const DemoGame = ({ topic, questions: rawQuestions, gameType, placeholderImage, onClose, onFinish, onSignup }) => {
  const [phase, setPhase] = useState("game"); // game → round → bonus → done
  const [quizScore, setQuizScore] = useState(0);
  const [bonusScore, setBonusScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const fired = useRef({ game: false, bonus: false });

  const questions = useMemo(() => shuffle(rawQuestions || []), [rawQuestions]);
  const game = useMemo(() => pickGame(gameType, questions), [gameType, questions]);
  const Component = lazyFor(game.key, game.load);
  const image = placeholderImage || topic.imageUrl || PLACEHOLDER;
  const grade = String(topic.grade || "3rd").split(",")[0].trim().toLowerCase();
  const entry = `/games/demo/${grade || "3rd"}/${topic.subject || "science"}/student/${topic.slug || "demo"}`;

  // full screen: stop the home page scrolling underneath, Esc closes
  useEffect(() => {
    const html = document.documentElement;
    const before = { html: html.style.overflow, body: document.body.style.overflow };
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      html.style.overflow = before.html;
      document.body.style.overflow = before.body;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const gameDone = useCallback((payload) => {
    if (fired.current.game) return;
    fired.current.game = true;
    setTimeout(() => {
      setQuizScore(scoreOf(payload));
      setAnswers(answersFromPayload(payload));
      setPhase("round");
    }, 0);
  }, []);

  const bonusDone = useCallback((payload) => {
    if (fired.current.bonus) return;
    fired.current.bonus = true;
    setTimeout(() => {
      setBonusScore(scoreOf(payload));
      setPhase("done");
    }, 0);
  }, []);

  const right = answers.filter((a) => a?.correct).length;
  const total = answers.length;

  const stage = () => {
    if (phase === "game") {
      const common = {
        questions,
        topic: { slug: topic.slug, title: topic.title, gameType, bonusRoundType: topic.bonusRoundType || "diamondTap", placeholderImage: image, videoUrl: "", musicUrl: "" },
        user: { uid: "demo", firstName: "Guest", displayName: "Guest" },
        placeholderImage: image,
        onComplete: gameDone,
        onBonusComplete: gameDone,
      };
      return game.math ? <Component {...common} mode={game.math.mode} value={game.math.value} /> : <Component {...common} />;
    }
    if (phase === "round") {
      return <RoundDone right={right} total={total} onGo={() => setPhase("bonus")} />;
    }
    if (phase === "bonus") {
      return (
        <BonusRouter
          slug={topic.slug}
          bonusRoundType={topic.bonusRoundType || "diamondTap"}
          questions={questions}
          placeholderImage={image}
          onComplete={bonusDone}
          grade={grade}
        />
      );
    }
    return (
      <GameDone
        title={topic.title}
        quizScore={quizScore}
        bonusScore={bonusScore}
        right={total ? right : 1}
        total={total || 1}
        onContinue={() => {
          onFinish?.({ topic, quizScore, bonusScore, right, total, answers });
          onClose?.();
        }}
      >
        <div className="pvd-teacher-peek">
          {answers.length > 0 && (
            <button type="button" className="pvd-link" onClick={() => setShowAnswers((v) => !v)}>
              👩‍🏫 {showAnswers ? "Hide" : "See"} what your teacher sees
            </button>
          )}
          {showAnswers && (
            <ol className="pvd-qa">
              {answers.map((a, i) => (
                <li key={i} className={a.correct ? "is-right" : "is-wrong"}>
                  <span className="pvd-qa-q">{a.question}</span>
                  <span className="pvd-qa-a">
                    {a.correct ? "✓" : "✗"} {a.selectedText || "(no answer)"}
                    {!a.correct && <em> — correct: {a.correctText}</em>}
                  </span>
                </li>
              ))}
            </ol>
          )}
          <p className="pvd-demo-note">This was a free demo — scores and coins aren't saved.</p>
          <button type="button" className="pvd-cta" onClick={onSignup}>
            ✨ Start my free trial
          </button>
        </div>
      </GameDone>
    );
  };

  return (
    <ModalPortal>
      <div className="pvd-game-stage" role="dialog" aria-modal="true" aria-label={`${topic.title} game`}>
        <div className="pvd-game-bar">
          <span className="pvd-game-tag">🎮 {topic.title} · free demo</span>
          <button type="button" className="pvd-game-x" onClick={onClose}>
            ✕ Close game
          </button>
        </div>
        <div className="pvd-game-body">
          <GameFrame entry={entry} onExit={onClose}>
            <Suspense fallback={<p className="pvd-game-msg">Loading the game…</p>}>{stage()}</Suspense>
          </GameFrame>
        </div>
      </div>
    </ModalPortal>
  );
};

export default DemoGame;
